<template>
  <div class="fts-design-element">
    <div class="element-designer-item-action-container">
      <fts-form-design-shortcut
        :modules="modules"
        :options="shortcutOptions"
        :elements="shortcutElements"
        :selected-options="shortcutSelectedOptions"
        suggestion
        placement="bottom-start"
        @option:selected="$emit('shortcut-option:selected', $event)"
        @option:create="$emit('shortcut-option:create', $event)"
        class="element-designer-item-action"
      >
        <fts-tippy>
          <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
          <div slot="content" class="tippy-hint">
            <span class="tippy-hint-highlight">Click</span> to add a field below
          </div>
        </fts-tippy>
      </fts-form-design-shortcut>
      <fts-dropdown-select
        append-to-body
        append-content-class="dropdown-menu"
        class="element-designer-item-action element-designer-item-action--drag-handle"
        @selected="(evt) => selectMenuAction(evt, item)"
      >
        <fts-tippy slot="toggle">
          <font-awesome-icon
            :icon="['fas', 'grip-vertical']"
            fixed-width
          />
          <template #content>
            <div class="tippy-hint">
              <span class="tippy-hint-highlight">Drag</span> to move
            </div>
            <div class="tippy-hint">
              <span class="tippy-hint-highlight">Click</span> to open menu
            </div>
          </template>
        </fts-tippy>
        <template #menu="{ select }">
          <div
            class="dropdown-option"
            @click="select('configure')"
            v-if="
              item.input !== 'empty' &&
              item.input !== 'section' &&
              item.input !== 'description' &&
              item.input !== 'image' &&
              !document
            "
          >
            <div class="dropdown-option-icon">
              <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
            </div>
            <div>Configure</div>
          </div>
          <div class="dropdown-option" @click="select('remove')">
            <div class="dropdown-option-icon">
              <font-awesome-icon :icon="['fas', 'times']" fixed-width />
            </div>
            <div>Remove</div>
          </div>
          <template v-if="
            item.input !== 'empty' &&
            item.input !== 'section' &&
            item.input !== 'description' &&
            item.input !== 'image' &&
            item.input !== 'button' &&
            item.input !== 'custom'
          ">
            <div class="dropdown-divider"></div>
            <div class="dropdown-option" @click="select('edit')">
              <div class="dropdown-option-icon">
                <font-awesome-icon :icon="['far', 'edit']" fixed-width />
              </div>
              <div>Edit Information</div>
            </div>
            <div class="dropdown-option dropdown-option--danger" @click="select('delete')">
              <div class="dropdown-option-icon">
                <font-awesome-icon :icon="['far', 'trash-alt']" fixed-width />
              </div>
              <div>Delete Information</div>
            </div>
          </template>
        </template>
      </fts-dropdown-select>
    </div>
    <div class="design-element-wrapper">
      <!-- custom:description -->
      <div
        class="design-element-custom-description"
        v-if="item.input == 'description'"
      >
        <fts-form-editor :content.sync="item.content" :el-parent-id="elParentId" />
      </div>
      <!-- custom:image -->
      <div
        class="design-element-custom-image"
        v-else-if="item.input == 'image'"
      >
        <fts-file-upload
          :vectorId="vectorId"
          :item="item.item"
          :formData="item"
        />
      </div>
      <!-- empty space -->
      <div
        class="design-element-empty-space"
        v-else-if="item.input == 'empty'"
      >
        <div>I help to reserve this space 🙈</div>
      </div>
      <!-- custom:button -->
      <div
        class="design-element-custom-button"
        v-else-if="item.input == 'button'"
      >
        <fts-btn
          outline
          :color="'primary'"
          text-transform-init
        >
          {{item.label ? item.label : "(unnamed)"}}
        </fts-btn>
      </div>
      <!-- section -->
      <template v-else-if="item.input === 'section'">
        <template v-if="item.type === 'tab'">
          <div
            v-for="(tab, tabIndex) in getTabs(item.tabs)"
            :key="tab.id"
            class="design-element-section design-element-section--tab"
          >
            <div class="design-element-section__header">
              <div class="design-element-section__header-l">
                <div class="design-element-section__header-tag">
                  {{ item.type }}
                </div>
                <fts-contenteditable
                  class="design-element-section__header-title"
                  v-model="tab.header"
                />
              </div>
              <div class="design-element-section__header-r">
                <div class="design-element-section__header-btn-wrapper">
                  <fts-btn
                    text
                    icon
                    sm
                    @click="removeTab(tab)"
                    v-if="getTabs(item.tabs).length > 1"
                  >
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </fts-btn>
                  <fts-btn
                    text
                    icon
                    sm
                    @click="addTab(tabIndex + 1)"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                  </fts-btn>
                </div>
              </div>
            </div>
            <div class="design-element-section__content">
              <fts-form-layouter
                :modules="modules"
                :form="tab.design"
                :form-data="formData"
                :activity-id="activityId"
                :activity-design="form"
                :item-group="'row'"
                :shortcut-options="shortcutOptions"
                :shortcut-elements="shortcutElements"
                :shortcut-selected-options="shortcutSelectedOptions"
                :document="document"
                @added="elementAdded"
              />
            </div>
          </div>
        </template>
        <div class="design-element-section" v-else>
          <div class="design-element-section__header">
            <div class="design-element-section__header-l">
              <div class="design-element-section__header-tag">
                {{ item.type }}
              </div>
              <fts-contenteditable
                class="design-element-section__header-title"
                v-model="item.header"
              />
            </div>
            <div class="design-element-section__header-r">
              <fts-toggle-switch
                :form-data="item"
                :item="{ fieldName: 'expanded' }"
                :size="18"
                :label-dir="'left'"
                :label-text="'Expanded by Default'"
                v-if="item.type === 'collapse'"
              />
            </div>
          </div>
          <div class="design-element-section__content">
            <fts-form-layouter
              :modules="modules"
              :form="item.design"
              :form-data="formData"
              :activity-id="activityId"
              :activity-design="form"
              :item-group="'row'"
              :shortcut-options="shortcutOptions"
              :shortcut-elements="shortcutElements"
              :shortcut-selected-options="shortcutSelectedOptions"
              :document="document"
              @added="elementAdded"
            />
          </div>
        </div>
      </template>
      <!-- design element default -->
      <div class="design-element__default" v-else>
        <template v-if="
          item.type !== 14 &&
          item.type !== 15 &&
          item.input !== 'table' &&
          item.input !== 'list'
        ">
          <fts-form-element
            :form-data="{}"
            :item="{
              ...item,
              required: Boolean(item.required || item.requiredFormula) && !item.readOnly && !item.formulaMode
            }"
            class="design-element-form-element"
            :class="[
              { 'design-element-form-element-signature': item.type === 29 },
              { 'design-element-form-element--invisible': item.visible === false },
              { 'design-element-form-element--readonly': item.readOnly }
            ]"
          />
          <div class="design-element-form-overlay"></div>
        </template>
        <fts-form-label
          :icon="getIcon(item.type)"
          icon-right
          placement="top"
          :required="Boolean(item.required || item.requiredFormula) && !item.readOnly && !item.formulaMode"
          class="design-element-form-label"
          :class="{ 'design-element-form-label--invisible': item.visible === false}"
          v-else
        >
          {{ item.label || item.displayName }}
        </fts-form-label>
        <!-- information:table -->
        <template v-if="item.type == 14">
          <draggable
            class="design-element-table-columns fts-scrollbar"
            :group="item.fieldName"
            :list="item.fields"
            v-bind="dragOptions"
            handle=".element-designer-item-action--drag-handle"
            draggable=".design-element-table-column"
            ref="designElementTableColumns"
          >
            <template v-for="(field, fieldIndex) in item.fields">
              <div
                class="design-element-table-column"
                :key="field.id"
                v-if="!field.hide"
              >
                <div class="design-element-table-column-element">
                  <div class="element-designer-item-action-container">
                    <fts-form-design-shortcut
                      :modules="modules"
                      :options="invisibleFields"
                      :selected-options="visibleFields"
                      :exclude-types="[14, 15]"
                      suggestion
                      placement="bottom-start"
                      class="element-designer-item-action"
                      @option:selected="(x) => showColumn(x, fieldIndex + 1)"
                      @option:create="(x) => createColumn(x, item, fieldIndex + 1)"
                    >
                      <fts-tippy>
                        <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                        <div slot="content" class="tippy-hint">
                          <span class="tippy-hint-highlight">Click</span> to add a field
                        </div>
                      </fts-tippy>
                    </fts-form-design-shortcut>
                    <fts-dropdown-select
                      append-to-body
                      append-content-class="dropdown-menu"
                      class="element-designer-item-action element-designer-item-action--drag-handle"
                      @selected="(evt) => selectMenuAction(evt, field, { parentId: item.id})"
                    >
                      <fts-tippy slot="toggle">
                        <font-awesome-icon
                          :icon="['fas', 'grip-vertical']"
                          fixed-width
                        />
                        <template #content>
                          <div class="tippy-hint">
                            <span class="tippy-hint-highlight">Drag</span> to move
                          </div>
                          <div class="tippy-hint">
                            <span class="tippy-hint-highlight">Click</span> to open menu
                          </div>
                        </template>
                      </fts-tippy>
                      <template #menu="{ select }">
                        <div
                          class="dropdown-option"
                          @click="select('configure')"
                          v-if="
                            item.input !== 'empty' &&
                            item.input !== 'section' &&
                            item.input !== 'description' &&
                            item.input !== 'image' &&
                            !document
                          "
                        >
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                          </div>
                          <div>Configure</div>
                        </div>
                        <div class="dropdown-option" @click="select('hideColumn')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['fas', 'times']" fixed-width />
                          </div>
                          <div>Remove</div>
                        </div>
                        <div class="dropdown-divider"></div>
                        <div class="dropdown-option" @click="select('edit')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['far', 'edit']" fixed-width />
                          </div>
                          <div>Edit Information</div>
                        </div>
                        <div class="dropdown-option dropdown-option--danger" @click="select('delete')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['far', 'trash-alt']" fixed-width />
                          </div>
                          <div>Delete Information</div>
                        </div>
                      </template>
                    </fts-dropdown-select>
                  </div>
                  <div class="design-element-table-column-form-element">
                    <fts-form-label
                      :icon="getIcon(field.type)"
                      icon-right
                      :required="Boolean(field.required || field.requiredFormula) && !field.readOnly && !field.formulaMode"
                      class="design-element-form-label"
                      :class="{ 'design-element-form-label--invisible': field.visible === false}"
                    >
                      {{ field.label || field.displayName }}
                    </fts-form-label>
                    <!-- shortcut -->
                    <div class="design-element-shortcut" v-if="!document">
                      <div class="design-element-shortcut-gap">
                        <template v-for="action in getActions(field)">
                          <template v-if="action === Required">
                            <fts-tippy :key="action.id" v-if="item.formulaMode || field.formulaMode">
                              <div>
                                <i class="fts-icon-asterisk design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                              </div>
                              <div slot="content" class="tippy-hint fts-text-left">
                                <div class="tippy-hint-highlight">Information formula mode</div>
                                <div>Values can't be directly edited.</div>
                              </div>
                            </fts-tippy>
                            <fts-tippy
                              :key="action.id"
                              content="set field as required"
                              v-else
                            >
                              <div
                                class="design-element-shortcut-toggle"
                                @click="updateItem(field, 'required')"
                              >
                                <i
                                  class="fts-icon-asterisk design-element-shortcut-action"
                                  :class="{ 'design-element-shortcut-action--active': field.required }"
                                ></i>
                              </div>
                            </fts-tippy>
                          </template>
                          <template v-else-if="action === ReadOnly">
                            <fts-tippy :key="action.id" v-if="item.formulaMode || field.formulaMode">
                              <div>
                                <i class="fts-icon-pencil-slash design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                              </div>
                              <div slot="content" class="tippy-hint fts-text-left">
                                <div class="tippy-hint-highlight">Information formula mode</div>
                                <div>Values can't be directly edited.</div>
                              </div>
                            </fts-tippy>
                            <fts-tippy
                              :key="action.id"
                              content="set field as view only"
                              v-else
                            >
                              <div
                                class="design-element-shortcut-toggle"
                                @click="updateItem(field, 'readOnly')"
                              >
                                <i
                                  class="fts-icon-pencil-slash design-element-shortcut-action"
                                  :class="{ 'design-element-shortcut-action--active': field.readOnly }"
                                ></i>
                              </div>
                            </fts-tippy>
                          </template>
                          <fts-tippy
                            :key="action.id"
                            content="set field as assign"
                            v-else-if="action === Assignee"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'assignee')"
                            >
                              <i
                                class="fts-icon-user-check design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.assignee }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field as due"
                            v-else-if="action === Due"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'due')"
                            >
                              <i
                                class="fts-icon-calendar-check design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.due }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field as addable"
                            v-else-if="action === Addable"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'disabledAddRemoveItems')"
                            >
                              <i
                                class="fts-icon-add-remove design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': !field.disabledAddRemoveItems }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field visibility"
                            v-else-if="action === Visible"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateVisible(field, 'visible')"
                            >
                              <i
                                class="fts-icon-hidden design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.visible === false }"
                              ></i>
                            </div>
                          </fts-tippy>
                        </template>
                        <!-- formula -->
                        <fts-tippy
                          key="formula"
                          content="set formula"
                          v-if="isFormulaAction(getActions(field))"
                        >
                          <div
                            class="design-element-shortcut-toggle"
                            @click="openFormulaModal(field, item)"
                          >
                            <i
                              class="fts-icon-formula design-element-shortcut-action"
                              :class="{ 'design-element-shortcut-action--active': isFormulaScript(field) }"
                            ></i>
                          </div>
                        </fts-tippy>
                      </div>
                    </div>
                    <!-- tag -->
                    <div class="design-element-tag design-element-tag--formula" v-if="!document">
                      <div class="design-element-tag-gap">
                        <div
                          class="design-element-tag-display"
                          @click="openFormulaModal(field, item)"
                          v-if="field.formula"
                        >
                          <i class="formula-tag-icon fts-icon-formula"></i>
                          <fts-formula-display
                            :variables="variables"
                            :form="form"
                            :formula="field.formula"
                          />
                        </div>
                        <div
                          class="design-element-tag-display"
                          @click="openFormulaModal(field, item)"
                          v-if="field.requiredFormula"
                        >
                          <i class="formula-tag-icon fts-icon-asterisk"></i>
                          <fts-formula-display
                            :variables="variables"
                            :form="form"
                            :formula="field.requiredFormula"
                          />
                        </div>
                        <div
                          class="design-element-tag-display"
                          @click="openFormulaModal(field, item)"
                          v-if="field.readOnlyFormula"
                        >
                          <i class="formula-tag-icon fts-icon-pencil-slash"></i>
                          <fts-formula-display
                            :variables="variables"
                            :form="form"
                            :formula="field.readOnlyFormula"
                          />
                        </div>
                        <div
                          class="design-element-tag-display"
                          @click="openFormulaModal(field, item)"
                          v-if="field.visibleFormula"
                        >
                          <i class="formula-tag-icon fts-icon-hidden"></i>
                          <fts-formula-display
                            :variables="variables"
                            :form="form"
                            :formula="field.visibleFormula"
                          />
                        </div>
                        <div
                          class="design-element-tag-display"
                          @click="selectMenuAction('configure', field, { parentId: item.id})"
                          v-if="field.queryScript"
                        >
                          <i class="formula-tag-icon fts-icon-code"></i>
                          <div>Query Script</div>
                        </div>
                        <div
                          class="design-element-tag-display"
                          @click="selectMenuAction('configure', field, { parentId: item.id})"
                          v-if="isQueryFilter(field.filter)"
                        >
                          <i class="formula-tag-icon fts-icon-filter"></i>
                          <div>Filter</div>
                        </div>     
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <fts-form-design-shortcut
              slot="footer"
              :modules="modules"
              :options="invisibleFields"
              :selected-options="visibleFields"
              :exclude-types="[14, 15]"
              suggestion
              placement="bottom-start"
              class="design-element-table-columm-create"
              @option:selected="showColumn"
              @option:create="(x) => createColumn(x, item)"
            >
              <fts-tippy appendTriggerClass="design-element-table-columm-create-toggle">
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                />
                <div slot="content" class="tippy-hint">
                  <span class="tippy-hint-highlight">Click</span> to add a field
                </div>
              </fts-tippy>
            </fts-form-design-shortcut>
          </draggable>
        </template>
        <!-- information:list -->
        <template v-else-if="item.type == 15">
          <fts-form-layouter
            :modules="modules"
            :activity-design="form"
            :activity-id="activityId"
            :form="item.design"
            :item="item"
            :item-group="item.fieldName"
            :form-data="{}"
            :shortcut-options="listInformation"
            :shortcut-selected-options="listClassificationInformation"
            :document="document"
            v-if="item.design"
          />
        </template>
      </div>
      <!-- shortcut -->
      <div class="design-element-shortcut" v-if="!document">
        <div class="design-element-shortcut-gap">
          <template v-for="action in getActions(item)">
            <template v-if="action === Required">
              <fts-tippy :key="action.id" v-if="item.formulaMode">
                <div>
                  <i class="fts-icon-asterisk design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                </div>
                <div slot="content" class="tippy-hint fts-text-left">
                  <div class="tippy-hint-highlight">Information formula mode</div>
                  <div>Values can't be directly edited.</div>
                </div>
              </fts-tippy>
              <fts-tippy
                :key="action.id"
                content="set field as required"
                v-else
              >
                <div class="design-element-shortcut-toggle" @click="updateItem(item, 'required')">
                  <i
                    class="fts-icon-asterisk design-element-shortcut-action"
                    :class="{ 'design-element-shortcut-action--active': item.required }"
                  ></i>
                </div>
              </fts-tippy>
            </template>
            <template v-else-if="action === ReadOnly">
              <fts-tippy :key="action.id" v-if="item.formulaMode">
                <div>
                  <i class="fts-icon-pencil-slash design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                </div>
                <div slot="content" class="tippy-hint fts-text-left">
                  <div class="tippy-hint-highlight">Information formula mode</div>
                  <div>Values can't be directly edited.</div>
                </div>
              </fts-tippy>
              <fts-tippy
                :key="action.id"
                content="set field as view only"
                v-else
              >
                <div
                  class="design-element-shortcut-toggle"
                  @click="updateItem(item, 'readOnly')"
                >
                  <i
                    class="fts-icon-pencil-slash design-element-shortcut-action"
                    :class="{ 'design-element-shortcut-action--active': item.readOnly }"
                  ></i>
                </div>
              </fts-tippy>
            </template>
            <fts-tippy
              :key="action.id"
              content="set field as assign"
              v-else-if="action === Assignee"
            >
              <div
                class="design-element-shortcut-toggle"
                @click="updateItem(item, 'assignee')"
              >
                <i
                  class="fts-icon-user-check design-element-shortcut-action"
                  :class="{ 'design-element-shortcut-action--active': item.assignee }"
                ></i>
              </div>
            </fts-tippy>
            <fts-tippy
              :key="action.id"
              content="remove assignee (only available for beta release)"
              v-else-if="action === Unassignee && beta"
            >
              <div
                class="design-element-shortcut-toggle"
                @click="updateItem(item, 'unassignee')"
              >
                <i
                  class="fts-icon-user design-element-shortcut-action"
                  :class="{ 'design-element-shortcut-action--active': item.unassignee }"
                ></i>
              </div>
            </fts-tippy>

            <fts-tippy
              :key="action.id"
              content="add assignee (only available for alpha release)"
              v-else-if="action === AddAssignee && isInternal"
            >
              <div
                class="design-element-shortcut-toggle"
                @click="updateItem(item, 'addAssignee')"
              >
                <i
                  class="fts-icon-user-plus design-element-shortcut-action"
                  :class="{ 'design-element-shortcut-action--active': item.addAssignee }"
                ></i>
              </div>
            </fts-tippy>

            <fts-tippy
              :key="action.id"
              content="set field as due"
              v-else-if="action === Due"
            >
              <div
                class="design-element-shortcut-toggle"
                @click="updateItem(item, 'due')"
              >
                <i
                  class="fts-icon-calendar-check design-element-shortcut-action"
                  :class="{ 'design-element-shortcut-action--active': item.due }"
                ></i>
              </div>
            </fts-tippy>
            <template v-else-if="action === Addable">
              <fts-tippy :key="action.id" v-if="item.formulaMode">
                <div>
                  <i class="fts-icon-add-remove design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                </div>
                <div slot="content" class="tippy-hint fts-text-left">
                  <div class="tippy-hint-highlight">Information formula mode</div>
                  <div>Values can't be directly edited.</div>
                </div>
              </fts-tippy>
              <fts-tippy
                :key="action.id"
                content="set field as addable"
                v-else
              >
                <div
                  class="design-element-shortcut-toggle"
                  @click="updateItem(item, 'disabledAddRemoveItems')"
                >
                  <i
                    class="fts-icon-add-remove design-element-shortcut-action"
                    :class="{ 'design-element-shortcut-action--active': !item.disabledAddRemoveItems }"
                  ></i>
                </div>
              </fts-tippy>
            </template>
            <fts-tippy
              :key="action.id"
              content="set field visibility"
              v-else-if="action === Visible"
            >
              <div
                class="design-element-shortcut-toggle"
                @click="updateVisible(item, 'visible')"
              >
                <i
                  class="fts-icon-hidden design-element-shortcut-action"
                  :class="{ 'design-element-shortcut-action--active': item.visible === false }"
                ></i>
              </div>
            </fts-tippy>
          </template>
          <!-- formula -->
          <fts-tippy
            key="formula"
            content="set formula"
            v-if="isFormulaAction(getActions(item))"
          >
            <div
              class="design-element-shortcut-toggle"
              @click="openFormulaModal(item)"
            >
              <i
                class="fts-icon-formula design-element-shortcut-action"
                :class="{ 'design-element-shortcut-action--active': isFormulaScript(item) }"
              ></i>
            </div>
          </fts-tippy>
        </div>
      </div>
      <!-- tag -->
      <div
        class="design-element-tag design-element-tag--formula"
        :class="{ 'design-element-tag--ml': item.type != 14 && item.type != 15 }"
        v-if="!document"
      >
        <div class="design-element-tag-gap">
          <div
            class="design-element-tag-display"
            @click="openFormulaModal(item)"
            v-if="informationFormula(item)"
          >
            <i class="formula-tag-icon fts-icon-formula"></i>
            <fts-formula-display
              :variables="variables"
              :form="form"
              :formula="informationFormula(item)"
            />
          </div>
          <div
            class="design-element-tag-display"
            @click="openFormulaModal(item)"
            v-else-if="item.formula"
          >
            <i class="formula-tag-icon fts-icon-formula"></i>
            <fts-formula-display
              :variables="variables"
              :form="form"
              :formula="item.formula"
            />
          </div>
          <div
            class="design-element-tag-display"
            @click="openFormulaModal(item)"
            v-if="item.requiredFormula"
          >
            <i class="formula-tag-icon fts-icon-asterisk"></i>
            <fts-formula-display
              :variables="variables"
              :form="form"
              :formula="item.requiredFormula"
            />
          </div>
          <div
            class="design-element-tag-display"
            @click="openFormulaModal(item)"
            v-if="item.readOnlyFormula"
          >
            <i class="formula-tag-icon fts-icon-pencil-slash"></i>
            <fts-formula-display
              :variables="variables"
              :form="form"
              :formula="item.readOnlyFormula"
            />
          </div>
          <div
            class="design-element-tag-display"
            @click="openFormulaModal(item)"
            v-if="item.visibleFormula"
          >
            <i class="formula-tag-icon fts-icon-hidden"></i>
            <fts-formula-display
              :variables="variables"
              :form="form"
              :formula="item.visibleFormula"
            />
          </div>
          <div
            class="design-element-tag-display"
            @click="selectMenuAction('configure', item)"
            v-if="item.queryScript"
          >
            <i class="formula-tag-icon fts-icon-code"></i>
            <div>Query Script</div>
          </div>
          <div
            class="design-element-tag-display"
            @click="selectMenuAction('configure', item)"
            v-if="isQueryFilter(item.filter)"
          >
            <i class="formula-tag-icon fts-icon-filter"></i>
            <div>Filter</div>
          </div>
        </div>
      </div>
    </div>
    <!-- form formula modal -->
    <fts-form-formula-modal ref="ftsFormFormulaModal" />
    <!-- information design modal -->
    <fts-information-design-modal ref="ftsInformationDesignModal" />
    <!-- information delete confirmation -->
    <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    <!-- fts-information-modal -->
    <fts-control-modal
      @ok="confirm($event)"
      :modalNameId="id"
    />
  </div>
</template>
<script>
import Vue from "vue";
import { store as canvasDesignStore} from '../store/canvasDesignStore';
import { authorizationStore } from '../store/authorizationStore';
import commons from '../commons/commons';
import canvasDesign from '../commons/canvasDesign';
import formDesign from '../commons/formDesign';

import draggable from 'vuedraggable';
import wysiwyg from 'vue-wysiwyg';
Vue.use(wysiwyg, {});

import FTSBtn from './FTSBtn';
import FTSContenteditable from './FTSContenteditable';
import FTSDropdownSelect from './FTSDropdownSelect';
import FTSFileUpload from './FTSFileUpload';
import FTSFormDesigner from './FTSFormDesigner';
import FTSFormLabel from './FTSFormLabel';
import FTSFormElement from './FTSFormElement';
import FTSTextArea from './FTSTextArea';
import FTSTippy from './FTSTippy';
import FTSToggleButton from './FTSToggleButton';
import FTSToggleSwitch from './FTSToggleSwitch';
import FTSFormEditor from './form/FTSFormEditor';
import FTSControlModal from '../design/FTSControlModal';
import FTSFormDesignShortcut from '../design/FTSFormDesignShortcut';
import FTSFormula from '../design/FTSFormula';
import FTSFormulaDisplay from '../design/FTSFormulaDisplay';
import FTSFormFormulaModal from '../design/FTSFormFormulaModal';
import FTSInformationModal from '../design/FTSInformationModal';
import FTSInformationDesignModal from '../design/FTSInformationDesignModal';
import FTSInformationDeleteConfirmation from '../design/FTSInformationDeleteConfirmation';

export default {
  components: {
    draggable,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-file-upload": FTSFileUpload,
    "fts-form-designer": FTSFormDesigner,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-text-area": FTSTextArea,
    "fts-tippy": FTSTippy,
    "fts-toggle-button": FTSToggleButton,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-form-editor": FTSFormEditor,
    "fts-control-modal": FTSControlModal,
    "fts-form-design-shortcut": FTSFormDesignShortcut,
    "fts-formula": FTSFormula,
    "fts-formula-display": FTSFormulaDisplay,
    'fts-form-formula-modal': FTSFormFormulaModal,
    "fts-information-modal": FTSInformationModal,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation
  },
  data() {
    return {
      id: null,
      formData: {},
      dragOptions: commons.dragOptions
    };
  },
  props: [
    "elParentId",
    "modules",
    "item",
    "document",
    "activityId",
    "form",
    "shortcutOptions",
    "shortcutElements",
    "shortcutSelectedOptions"
  ],
  watch: {
    "item.type": function () {
      if (this.item.type == "tab") {
        if (this.item.tabs == null) {
          Vue.set(this.item, "tabs", [
            {
              id: commons.newUUID(),
              header: "(new tab)",
              design: { rows: [{ index: commons.newUUID(), items: [] }] },
            },
          ]);
        }
      }
    },
  },
  methods: {
    getTabs(tabs) {
      if (!tabs.find((x) => x.selected) && tabs && tabs.length) {
        Vue.set(tabs[0], "selected", true);
      }
      return tabs;
    },
    addTab(index) {
      this.item.tabs.splice(index || 0, 0 , {
        id: commons.newUUID(),
        header: "(new tab)",
        design: { rows: [] }
      });
    },
    removeTab(tab) {
      let ind = this.item.tabs.indexOf(tab);
      this.item.tabs.splice(ind, 1);
    },
    elementAdded(element) {
      this.$emit("added", element);
    },
    async remove() {
      const isFormElement = formDesign.isFormElement(this.item.input);
      if (isFormElement) {
        this.$emit("remove", this.item);
      } else {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const forms = formDesign.getFormsByInformationId(
          canvas,
          this.item.id,
          { excluded: [this.activityId]}
        );
        if (forms && forms.length) {
          this.$emit("remove", this.item);
        } else {
          const res = await this.$refs.ftsInformationDeleteConfirmation.show({
            canvas: canvas,
            information: this.item,
            forms: forms,
            type: 'remove'
          });
          if (res === 'remove') {
            this.$emit("remove", this.item);
          } else if (res === true) {
            this.deleteInformation(canvas, this.item);
          }
        }
      }
    }, 
    getIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    hideColumn(column) {
      this.$set(column, "hide", true);
    },
    showColumn(item, index) {
      let column = this.item.fields.find((x) => x.id === item.id);
      if (index) {
        this.item.fields.splice(this.item.fields.indexOf(column), 1);
        this.item.fields.splice(index, 0, column);
      }
      column && this.$delete(column, "hide");
    },
    async createColumn(item, parent, index) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDesignModal.show(
        canvasDesign.initInformation(item),
        null, null, { parentId: parent.id }
      );
      if (res && res !== true) {
        canvasDesign.setInformation(canvas, _.cloneDeep(res), parent.id);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        if (!Array.isArray(parent.fields)) {
          this.$set(parent, "fields", [res]);
        } else if (index) {
          parent.fields.splice(index, 0 , res);
        } else {
          parent.fields.push(res);
        }
      }
    },
    configure(item) {
      let formData = item;
      let form = this.form;
      this.$modal.show("controlModal" + this.id, { formData, form });
    },
    updateItem(item, arg) {
      if (
        item[arg] === null ||
        item[arg] === undefined ||
        item[arg] === false
      ) {
        this.$set(item, arg, true);
        if (arg === this.Assignee) {
          this.$delete(item, this.Unassignee);
          this.$delete(item, this.AddAssignee);
        } else if (arg === this.Unassignee) {
          this.$delete(item, this.Assignee);
          this.$delete(item, this.AddAssignee);
        } else if (arg === this.AddAssignee) {
          this.$delete(item, this.Assignee);
          this.$delete(item, this.Unassignee);
        }
      } else {
        this.$delete(item, arg);
      }
    },
    updateVisible(item, arg) {
      if (item[arg] === false) {
        this.$delete(item, arg);
      } else {
        this.$set(item, arg, false);
      }
    },
    confirm(formData) {
      /* update table columns */
      if (this.item.type === 14 && this.item.fields) {
        let column = this.item.fields.find((x) => x.id === formData.id);
        if (column) {
          column = formData;
          return;
        }
      }
      /* update information */
      this.item = formData;
    },
    isFormulaScript(item) {
      return Boolean(
        item.formula ||
        item.requiredFormula ||
        item.readOnlyFormula ||
        item.visibleFormula
      );
    },
    informationFormula(item) {
      if (item.formulaMode) {
        try {
          const res = _.findDeep(this.information, (x) => x.id == item.id, {
            childrenPath: 'fields'
          });
          return res && res.value && res.value.formula;
        } catch(err) { console.error(err); }
      }
    },
    async openFormulaModal(data, parent) {
      const res = await this.$refs.ftsFormFormulaModal.show({
        formData: data,
        variables: this.variables,
        form: this.form,
        actions: this.getActions(data),
        informationFormula: this.informationFormula(data),
        isParentFormulaMode: Boolean(parent && parent.formulaMode)
      });
      if (res) {
        if (parent && parent.fields) {
          const index = parent.fields.findIndex((x) => x.id == data.id);
          if (index > -1) {
            this.$set(parent.fields, index, res);
          }
        } else {
          this.$emit('update:item', res);
        }
      }
    },
    getActions(item) {
      if (item.input == "description" || item.input == "image" || item.input == "empty") {
        return [this.Visible];
      } else if (item.input == "button") {
        return [this.ReadOnly, this.Visible];
      } else if (
        item.type == 5 ||
        item.type == 6 ||
        item.type == 30 ||
        item.type == 31
      ) {
        return [this.Required, this.ReadOnly, this.Due, this.Visible, this.Formula];
      } else if (item.type == 14 || item.type == 15) {
        return [this.Required, this.ReadOnly, this.Addable, this.Visible, this.Formula];
      } else if (item.type == 20 || item.type == 21) {
        return [this.Required, this.ReadOnly, this.Assignee, this.Unassignee, this.AddAssignee, this.Visible, this.Formula];
      } else if (item.input == "section") {
        return [this.Visible];
      } else {
        return [this.Required,this.ReadOnly, this.Visible, this.Formula];
      }
    },
    isFormulaAction(actions) {
      return [this.Formula, this.Required, this.ReadOnly, this.Visible].some(x => actions.indexOf(x) > -1);
    },
    isQueryFilter(filter) {
      return filter && Array.isArray(filter.items) ? Boolean(filter.items.length) : false;
    },
    selectMenuAction(action, item, payload = {}) {
      if (action === 'configure') {
        this.configure(item);
      } else if (action === 'edit') {
        this.openInformationDesignModal(item, payload.parentId);
      } else if (action === 'delete') {
        this.openInformationDeleteConfirmation(item);
      } else if (action === 'remove') {
        this.remove();
      } else if (action === 'hideColumn') {
        this.hideColumn(item);
      }
    },
    deleteInformation(canvas, item) {
      canvasDesign.deleteInformation(canvas, item.id);
      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      if (this.item.type == 14 && this.item.id !== item.id) {
        commons.expandWorkDesignInternal(this.information, this.item);
      } else {
        this.$emit("remove", this.item);
      }
    },
    async openInformationDeleteConfirmation(item) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDeleteConfirmation.show({
        canvas: canvas,
        information: item,
        payload: { excluded: [this.activityId]}
      });
      if (res === true) {
        this.deleteInformation(canvas, item);
      }
    },
    async openInformationDesignModal(item, parentId) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const information = canvasDesign.getInformation(canvas, item.id);
      if (information) {
        const res = await this.$refs.ftsInformationDesignModal.show(
          {...canvasDesign.initInformation(information)},
          null, null, { activityId: this.activityId }
        );
        if (res && res !== true) {
          canvasDesign.setInformation(canvas, res, parentId);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          if (parentId) {
            if (this.item.type == 14) {
              const parent = canvasDesign.getInformation(canvas, parentId);
              if (parent && Array.isArray(parent.fields)) {
                commons.expandWorkDesignInternal(parent.fields, item);
              }
            }
          } else {
            // to-do update list field
            commons.expandWorkDesignInternal(this.information, item);
            this.$emit("update:item", item);
          }
        } else if (res === true) {
          this.deleteInformation(canvas, information);
        }
      } else {
        this.$toasted.global.error(`Something went wrong. Information #${item.displayName} not found.`);
      }
    }
  },
  computed: {
    variables() {
      return [
        {
          name: "Form",
          fieldName: "form",
          type: 35,
          moduleId: this.$store.state.vectorId.toString(),
          activityId: this.activityId,
          form: this.form,
        },
        {
          name: 'Entry',
          fieldName: 'entry',
          type: 7,
          moduleId: this.$store.state.vectorId.toString(),
        }
      ];
    },
    information() {
      return this.$store.state.canvas.information;
    },
    vectorId() {
      return this.$store.state.vectorId;
    },
    invisibleFields() {
      return this.item.fields.filter(x => x.hide);
    },
    visibleFields() {
      return this.item.fields.filter(x => !x.hide);
    },
    listClassificationInformation() {
      return commons.classificationInformation(this.item.design.rows, this.item.fields);
    },
    listInformation() {
      return this.item.fields.filter(x =>
        !this.listClassificationInformation.some(y => y.id === x.id)
      );
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
  },
  created() {
    this.Formula = 'formula';
    this.Required = 'required';
    this.ReadOnly = 'readOnly';
    this.Visible = 'visible';
    this.Assignee = 'assignee';
    this.Unassignee = 'unassignee';
    this.AddAssignee = 'addAssignee';
    this.Due = 'due';
    this.Addable = 'addable';
  },
  mounted() {
    this.id = this._uid;
  }
};
</script>